import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/apiClient';

const LIST_KEY = ['notifications', 'list'] as const;
const DEVICES_KEY = ['notifications', 'devices'] as const;
const LIMITS_KEY = ['notifications', 'limits'] as const;

export type NotificationTargetType =
  | 'none'
  | 'product'
  | 'collection'
  | 'order'
  | 'url';

export interface NotificationTarget {
  type: NotificationTargetType;
  value: string;
}

export interface NotificationAudience {
  platforms: ('ios' | 'android' | 'web')[];
  locales: string[];
  login_status: 'any' | 'logged_in' | 'guest';
}

export interface NotificationItem {
  id: number;
  title: string;
  body: string;
  image_url: string | null;
  link_url: string | null;
  audience: NotificationAudience | Record<string, unknown>;
  sent_count: number;
  delivered_count: number;
  failed_count: number;
  opened_count: number;
  clicked_count: number;
  converted_count: number;
  status: 'sending' | 'sent' | 'partial' | 'failed';
  created_at: number;
  stats_updated_at: number | null;
}

export interface DeviceItem {
  id: number;
  platform: 'ios' | 'android' | 'web';
  locale: string | null;
  customer_id: string | null;
  last_seen: number;
  created_at: number;
  disabled: boolean;
}

export type ImageCrop = 'auto' | 'square' | 'landscape' | 'portrait' | 'hidden';

export interface SendRequest {
  title: string;
  body: string;
  image_url?: string;
  image_crop?: ImageCrop;
  image_bg_color?: string;
  target?: NotificationTarget;
  audience?: Partial<NotificationAudience>;
}

export interface SendResponse {
  ok: boolean;
  notification_id: number;
  sent: number;
  failed: number;
  reach: number;
}

export interface TestRequest extends SendRequest {
  device_id: number;
}

export function useNotifications(limit = 50, offset = 0) {
  return useQuery({
    queryKey: [...LIST_KEY, limit, offset],
    queryFn: () =>
      apiRequest<{ items: NotificationItem[]; total: number }>(
        `/api/admin/notifications?limit=${limit}&offset=${offset}`,
      ),
    staleTime: 10 * 1000,
  });
}

export function useNotificationStats(
  id: number | null,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['notifications', 'stats', id],
    queryFn: () =>
      apiRequest<NotificationItem & { refreshed: { delivered: number; checked: number } }>(
        `/api/admin/notifications/${id}/stats`,
      ),
    enabled: id != null && (options?.enabled ?? true),
    staleTime: 0,
  });
}

export function useDevices() {
  return useQuery({
    queryKey: DEVICES_KEY,
    queryFn: () =>
      apiRequest<{ items: DeviceItem[] }>(
        '/api/admin/notifications/devices',
      ),
    staleTime: 30 * 1000,
  });
}

export function useNotificationLimits() {
  return useQuery({
    queryKey: LIMITS_KEY,
    queryFn: () =>
      apiRequest<{ title_max: number; body_max: number }>(
        '/api/admin/notifications/limits',
      ),
    staleTime: Infinity,
  });
}

export function useSendNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SendRequest) =>
      apiRequest<SendResponse>('/api/admin/notifications/send', {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
    },
  });
}

export function useTestNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TestRequest) =>
      apiRequest<SendResponse>('/api/admin/notifications/test', {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
    },
  });
}

export function useAudiencePreview() {
  return useMutation({
    mutationFn: (audience: Partial<NotificationAudience>) =>
      apiRequest<{ count: number; audience: NotificationAudience }>(
        '/api/admin/notifications/audience-preview',
        { method: 'POST', body: { audience } },
      ),
  });
}

// Lightweight pickers for the target dropdown. We reuse the existing
// /api/sections/{products,collections} proxy endpoints — same shape.
export interface PickerItem {
  value: string;   // handle / id
  title: string;
  thumbnail: string;
}

export function useProductPicker() {
  return useQuery({
    queryKey: ['picker', 'products'],
    queryFn: () =>
      apiRequest<{ items: PickerItem[] }>('/api/sections/products'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCollectionPicker() {
  return useQuery({
    queryKey: ['picker', 'collections'],
    queryFn: () =>
      apiRequest<{ items: PickerItem[] }>('/api/sections/collections'),
    staleTime: 5 * 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Device groups
// ---------------------------------------------------------------------------

export interface DeviceGroupMember {
  id: number;
  platform: 'ios' | 'android' | 'web';
  locale: string | null;
  customer_id: string | null;
  last_seen: number;
}

export interface DeviceGroup {
  id: number;
  name: string;
  created_at: number;
  updated_at: number;
  members: DeviceGroupMember[];
  member_count: number;
}

const GROUPS_KEY = ['notifications', 'groups'] as const;

export function useDeviceGroups() {
  return useQuery({
    queryKey: GROUPS_KEY,
    queryFn: () =>
      apiRequest<{ items: DeviceGroup[] }>('/api/admin/notifications/groups'),
    staleTime: 30 * 1000,
  });
}

export function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; device_ids: number[] }) =>
      apiRequest<DeviceGroup>('/api/admin/notifications/groups', {
        method: 'POST',
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: GROUPS_KEY }),
  });
}

export function useUpdateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: number; name?: string; device_ids?: number[] }) => {
      const { id, ...body } = args;
      return apiRequest<DeviceGroup>(`/api/admin/notifications/groups/${id}`, {
        method: 'PUT',
        body,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: GROUPS_KEY }),
  });
}

export function useDeleteGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiRequest<{ ok: boolean }>(`/api/admin/notifications/groups/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: GROUPS_KEY }),
  });
}

export interface TestGroupRequest extends SendRequest {
  group_id: number;
}

export function useTestGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TestGroupRequest) =>
      apiRequest<SendResponse>('/api/admin/notifications/test-group', {
        method: 'POST',
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications', 'list'] }),
  });
}

// ---------------------------------------------------------------------------
// Phase 2B — Schedules
// ---------------------------------------------------------------------------

export type ScheduleType = 'one_time' | 'recurring' | 'trigger';
export type TriggerKind = 'install_plus_hours' | 'signup' | 'cart_idle' | 'price_drop';

export interface NotificationSchedule {
  id: number;
  title: string;
  body: string;
  image_url: string | null;
  image_crop: ImageCrop;
  image_bg_color: string | null;
  link_url: string | null;
  audience: NotificationAudience | Record<string, unknown>;
  schedule_type: ScheduleType;
  fire_at: number | null;
  cron_expr: string | null;
  trigger_kind: TriggerKind | null;
  trigger_params: Record<string, unknown> | null;
  enabled: boolean;
  last_fired_at: number | null;
  fire_count: number;
  created_at: number;
  updated_at: number;
}

export interface CreateScheduleRequest {
  title: string;
  body: string;
  image_url?: string;
  image_crop?: ImageCrop;
  image_bg_color?: string;
  target?: NotificationTarget;
  audience?: Partial<NotificationAudience>;
  schedule_type: ScheduleType;
  fire_at?: number;
  cron_expr?: string;
  trigger_kind?: TriggerKind;
  trigger_params?: Record<string, unknown>;
}

const SCHEDULES_KEY = ['notifications', 'schedules'] as const;

export function useSchedules() {
  return useQuery({
    queryKey: SCHEDULES_KEY,
    queryFn: () =>
      apiRequest<{ items: NotificationSchedule[] }>(
        '/api/admin/notifications/schedules',
      ),
    staleTime: 30 * 1000,
  });
}

export function useCreateSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateScheduleRequest) =>
      apiRequest<NotificationSchedule>('/api/admin/notifications/schedule', {
        method: 'POST',
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: SCHEDULES_KEY }),
  });
}

export function useToggleSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: number; enabled: boolean }) =>
      apiRequest<NotificationSchedule>(
        `/api/admin/notifications/schedules/${args.id}`,
        { method: 'PUT', body: { enabled: args.enabled } },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: SCHEDULES_KEY }),
  });
}

export function useDeleteSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiRequest<{ ok: boolean }>(
        `/api/admin/notifications/schedules/${id}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: SCHEDULES_KEY }),
  });
}

/** Trigger schedules only: edit copy + trigger params in place. */
export interface UpdateTriggerScheduleRequest {
  id: number;
  enabled?: boolean;
  title?: string;
  body?: string;
  trigger_params?: Record<string, number | null>;
}

export function useUpdateTriggerSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateTriggerScheduleRequest) =>
      apiRequest<NotificationSchedule>(
        `/api/admin/notifications/schedules/${id}`,
        { method: 'PUT', body },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: SCHEDULES_KEY }),
  });
}

// ---------------------------------------------------------------------------
// Carts — live per-device cart state (cart reminder / price-drop triggers)
// ---------------------------------------------------------------------------

export type CartStatus = 'active' | 'empty' | 'purchased';

export interface CartOverviewLine {
  title: string | null;
  variant: string | null;
  quantity: number;
  unit_price: number | null;
  handle: string | null;
}

export interface CartOverviewItem {
  device_id: number;
  platform: 'ios' | 'android' | 'web';
  customer_id: string | null;
  status: CartStatus;
  item_count: number;
  subtotal: number;
  currency: string | null;
  lines: CartOverviewLine[];
  started_at: number | null;
  updated_at: number;
  purchased_at: number | null;
  last_push_at: number | null;
}

export interface CartsOverview {
  summary: {
    active: number;
    idle: number;
    purchased: number;
    emptied: number;
    open_value: number;
    idle_hours: number;
    sent_30d: Partial<Record<'cart_idle' | 'price_drop', number>>;
  };
  items: CartOverviewItem[];
}

export function useCartsOverview(idleHours: number) {
  return useQuery({
    queryKey: ['notifications', 'carts', idleHours],
    queryFn: () =>
      apiRequest<CartsOverview>(
        `/api/admin/notifications/carts?limit=100&idle_hours=${idleHours}`,
      ),
    staleTime: 30 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

export interface NotificationTemplate {
  id: number;
  name: string;
  title: string;
  body: string;
  image_url: string | null;
  image_crop: ImageCrop;
  image_bg_color: string | null;
  link_url: string | null;
  target_type: string | null;
  target_value: string | null;
  audience: NotificationAudience | Record<string, unknown>;
  created_at: number;
  updated_at: number;
  use_count: number;
}

export interface CreateTemplateRequest {
  name: string;
  title: string;
  body: string;
  image_url?: string;
  image_crop?: ImageCrop;
  image_bg_color?: string;
  target?: NotificationTarget;
  audience?: Partial<NotificationAudience>;
}

const TEMPLATES_KEY = ['notifications', 'templates'] as const;

export function useTemplates() {
  return useQuery({
    queryKey: TEMPLATES_KEY,
    queryFn: () =>
      apiRequest<{ items: NotificationTemplate[] }>(
        '/api/admin/notifications/templates',
      ),
    staleTime: 60 * 1000,
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTemplateRequest) =>
      apiRequest<NotificationTemplate>('/api/admin/notifications/templates', {
        method: 'POST',
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: TEMPLATES_KEY }),
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiRequest<{ ok: boolean }>(
        `/api/admin/notifications/templates/${id}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: TEMPLATES_KEY }),
  });
}

// ---------------------------------------------------------------------------
// Dashboard overview
// ---------------------------------------------------------------------------

export interface NotificationOverview {
  active_devices: number;
  last_7_days: {
    sent: number;
    opened: number;
    clicked: number;
    converted: number;
    notifications: number;
  };
  sparkline: number[];
  latest: NotificationItem | null;
  active_schedules: number;
}

// ---------------------------------------------------------------------------
// A/B testing
// ---------------------------------------------------------------------------

export interface AbVariantStats {
  notification_id: number;
  title: string;
  body: string;
  sent: number;
  opened: number;
  clicked: number;
  converted: number;
}

export interface AbGroup {
  id: string;
  created_at: number;
  variants: Record<'A' | 'B', AbVariantStats | undefined>;
}

export interface AbSendRequest {
  variant_a: { title: string; body: string };
  variant_b: { title: string; body: string };
  split_a: number;
  image_url?: string;
  image_crop?: ImageCrop;
  image_bg_color?: string;
  target?: NotificationTarget;
  audience?: Partial<NotificationAudience>;
}

export interface AbSendResponse {
  ok: boolean;
  ab_group_id: string | null;
  variant_a: AbVariantStats & { failed: number; reach: number };
  variant_b: AbVariantStats & { failed: number; reach: number };
}

const AB_GROUPS_KEY = ['notifications', 'ab-groups'] as const;

export function useAbGroups() {
  return useQuery({
    queryKey: AB_GROUPS_KEY,
    queryFn: () =>
      apiRequest<{ items: AbGroup[] }>('/api/admin/notifications/ab-groups'),
    staleTime: 30 * 1000,
  });
}

export function useSendAbTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AbSendRequest) =>
      apiRequest<AbSendResponse>('/api/admin/notifications/ab-send', {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: AB_GROUPS_KEY });
      qc.invalidateQueries({ queryKey: ['notifications', 'list'] });
      qc.invalidateQueries({ queryKey: ['notifications', 'overview'] });
    },
  });
}

export function useNotificationOverview() {
  return useQuery({
    queryKey: ['notifications', 'overview'],
    queryFn: () =>
      apiRequest<NotificationOverview>(
        '/api/admin/notifications/overview',
      ),
    staleTime: 30 * 1000,
    refetchOnMount: true,
  });
}

export function useMarkTemplateUsed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiRequest<{ ok: boolean }>(
        `/api/admin/notifications/templates/${id}/use`,
        { method: 'POST' },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: TEMPLATES_KEY }),
  });
}
